import { Search, X } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { ColumnMatchMode } from '@/types/filters';

interface TextFilterProps {
  value: string;
  onChange: (value: string, mode?: ColumnMatchMode) => void;
  mode?: ColumnMatchMode;
  placeholder?: string;
}

export const TextFilter: React.FC<TextFilterProps> = ({
  value,
  onChange,
  mode,
  placeholder = 'Filter...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [tempMode, setTempMode] = useState<ColumnMatchMode>(mode ?? 'contains');

  const handleApply = () => {
    onChange(tempValue, mode === undefined ? undefined : tempMode);
    setIsOpen(false);
  };

  const handleClear = () => {
    setTempValue('');
    setTempMode('contains');
    onChange('', mode === undefined ? undefined : 'contains');
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Initialize temp value with current value when opening
      setTempValue(value);
      setTempMode(mode ?? 'contains');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleApply();
    }
  };

  const hasValue = value.length > 0;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="h-4 w-4 text-text-muted hover:text-text-primary cursor-pointer relative"
          title={hasValue ? value : placeholder}
        >
          <Search className="h-4 w-4 translate-y-0.5" />
          {hasValue && (
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
              •
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="space-y-3">
          {mode !== undefined && (
            <label className="block text-sm">
              Vergleich
              <select
                aria-label="Vergleich"
                className="block w-full border rounded px-2 py-1"
                value={tempMode}
                onChange={(e) => setTempMode(e.target.value as ColumnMatchMode)}
              >
                <option value="contains">Enthält</option>
                <option value="equals">Ist gleich</option>
              </select>
            </label>
          )}
          {mode !== undefined && tempMode === 'equals' && (
            <p className="text-xs text-text-muted">
              Vollständigen Wert eingeben, auch historische Werte.
              Groß-/Kleinschreibung, Akzente und abschließende Leerzeichen
              werden gleich behandelt.
            </p>
          )}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {tempValue && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTempValue('')}
                className="h-6 w-6 p-0 hover:bg-gray-100"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-xs"
            >
              Löschen
            </Button>
            <Button size="sm" onClick={handleApply} className="text-xs">
              Anwenden
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
